import { Injectable } from '@angular/core';
import { ContentService } from '@blk/cwp-ng-framework';
import { ERROR_SEVERITY_TO_TRACK } from '@ddt-web/config';
import * as cloneDeep from 'lodash/cloneDeep';
import * as get from 'lodash/get';
import * as has from 'lodash/has';
import * as isEmpty from 'lodash/isEmpty';
import * as isNil from 'lodash/isNil';
import * as omitBy from 'lodash/omitBy';

import { TealiumConstants } from '../constants/tealium.constants';
import { IGlobalAnalyticsParams } from '../models/global-analytics-params.model';
import { TealiumAnalyticsEvent } from '../models/tealium-analytics-event';
import { ITealiumAnalyticsOptions } from '../models/tealium-analytics-options';
import { ITealiumPageChangeData } from '../models/tealium-page-change-data.model';
import { TealiumToolState } from '../models/tealium-tool-state.enum';
import { GlobalAnalyticsMapperUtil } from '../utils/global-analytics-mapper.util';
import { PortfolioIdResolverUtil } from '../utils/portfolio-id-resolver.util';

@Injectable()
export class TealiumAnalyticsService {
	public tealiumAnalytics: any;
	public trackedComponentName: string = '';
	public toolName: string = '';
	public event: TealiumAnalyticsEvent;
	public globalAnalyticsParams: IGlobalAnalyticsParams;
	private errorSeverityToTrackList: Array<string>;

	private queueOfEvents: Array<any> = [];

	constructor(private contentService: ContentService) {

		try {
			const trackedName = this.contentService.getAppContainerProperties()['trackedName'];
			this.trackedComponentName = trackedName ? trackedName + ':' : '';
			this.errorSeverityToTrackList = this.contentService.get('ERROR_SEVERITY_TO_TRACK', ERROR_SEVERITY_TO_TRACK).split(',');
		} catch (err) {
			console.error('Failed to initialize the tealium analytics service with the current component!');
		}

		this.initGlobalAnalyticsData();
		window.addEventListener('load', (event) => {
			if (document.readyState === 'complete') {
				if (window['utag']) {
					console.log('Setting utag');
					this.tealiumAnalytics = window['utag'];
					this.trackEventQueue();
				} else {
					console.error('Document state complete but utag not found');
				}
			}
		});
	}

	private trackEventQueue(): void {
		this.queueOfEvents.forEach(event => {
			if (event === 'view') {
				console.log('track page view from queue');
				this.triggerPageView();
			} else {
				console.log('track tealium event from queue');
				this.trackEventTealium(event);
			}
		});
	}
	private updateEventQueue(event: any): void {
		console.log('Adding event in queue:', event);
		this.queueOfEvents.push(event);
	}

	/**
	 * Function to track start, complete and generic events using tealium
	 * @param {TealiumAnalyticsEvent} event
	 */
	public trackEventTealium(event: TealiumAnalyticsEvent): void {
		this.tealiumAnalytics = window['utag'];
		// we have an event but don't have options let's set our default options
		if (event && !event.getOptions()) {
			this.event = new TealiumAnalyticsEvent(event.getEventName(), {});
		} else {
			this.event = event;
		}

		if (this.tealiumAnalytics && typeof this.tealiumAnalytics.link === 'function' && this.event) {
			this.updateTrackedComponentName();
			this.tealiumAnalytics.link(this.constructEventData());
		} else {
			this.updateEventQueue(this.event);
		}
	}

	/**
	 * Trigger page view event
	 */
	public triggerPageView(options?: Object): void {
		if (this.tealiumAnalytics && typeof this.tealiumAnalytics.view === 'function') {
			if (options) {
				this.tealiumAnalytics.view(options);
			} else {
				this.tealiumAnalytics.view();
			}
		} else {
			this.updateEventQueue('view');
		}
	}

	/**
	 * Trigger tool start event
	 */
	public triggerToolStart(toolName: string): void {
		GlobalAnalyticsMapperUtil.setToolState(TealiumToolState.STARTED);

		this.trackEventTealium(new TealiumAnalyticsEvent(TealiumConstants.TOOLS_EVENT.START, {
			toolName,
			eventNumber: TealiumConstants.EVENT_NUMBERS.START_EVENT
		}));
	}

	/**
	 * Trigger tool completion event
	 * @param {string} portfolioIds comma separated list of portfolio ids
	 */
	public triggerToolCompletion(portfolioIds?: string, toolName?: string, toolFlow?: string): void {
		GlobalAnalyticsMapperUtil.setToolState(TealiumToolState.COMPLETED);

		const toolCompletionEvent = new TealiumAnalyticsEvent(TealiumConstants.TOOLS_EVENT.COMPLETION, {});
		if (portfolioIds) {
			GlobalAnalyticsMapperUtil.setPortfolioIds(portfolioIds);
			toolCompletionEvent.setOptions('portfolioIds', portfolioIds);
		}
		if (toolName) {
			toolCompletionEvent.setOptions('toolName', toolName);
		}
		if (toolFlow) {
			toolCompletionEvent.setOptions('toolFlow', toolFlow);
		}

		this.trackEventTealium(toolCompletionEvent);
	}

	/**
	 * Logic which checks for the tool state, and triggers tool starts and/or completes accordingly.
	 * @param portfolioIds comma separated list of portfolio ids
	 * @param toolName name of the tool being tracked
	 */
	public trackToolStartAndCompletion(portfolioIds?: string, toolName?: string) {
		const toolState = GlobalAnalyticsMapperUtil.getToolState();
		const isFirstTimePageLoad = !GlobalAnalyticsMapperUtil.getPortfolioIds();

		if (isFirstTimePageLoad) {
			if (!toolState) {
				this.triggerToolStart(toolName);
			}
			this.triggerToolCompletion(portfolioIds, toolName);
		}
	}

	/**
	 * Function to track errors using tealium
	 *
	 * @param {string} errorMessage
	 * @param {string} errorSeverity
	 */
	public trackError(errorMessage: string, errorSeverity: string): void {
		if (this.errorSeverityToTrackList.includes(errorSeverity)) {
			const trackErrorMessage = errorSeverity + ':' + errorMessage;
			const event = new TealiumAnalyticsEvent(TealiumConstants.TOOLS_EVENT.ERROR, { errorMessage: trackErrorMessage, errorSeverity: errorSeverity });
			this.trackEventTealium(event);
		}
	}

	/**
	 * @description trackPageChangeEvent: used to track page change events - specifically used in a SPA architecture.
	 * @param {string} pageName the name of the page.
	 * @param {string} pageUrl the url of the page.
	 */
	public trackPageChangeEvent(pageName: string, pageUrl: string): void {
		if (this.tealiumAnalytics) {
			this.tealiumAnalytics.view(this.buildPageChangeEvent(pageName, pageUrl));
		}
	}

	private buildPageChangeEvent(pageName: string, pageUrl: string): ITealiumPageChangeData {
		return <ITealiumPageChangeData>{ page_name: pageName, page_url: pageUrl };
	}

	private initGlobalAnalyticsData(): void {
		if (!window['globalAnalyticsParams']) {
			window['globalAnalyticsParams'] = {};
		}
		GlobalAnalyticsMapperUtil.setToolName();
		GlobalAnalyticsMapperUtil.setMode();
		GlobalAnalyticsMapperUtil.setTrackingVariable();
	}

	private updateTrackedComponentName(): void {
		if (has(this.event.getOptions(), 'trackedComponentName')) {
			this.trackedComponentName = this.event.getOptions()['trackedComponentName'] + ':';
		}
	}

	private constructEventData(): { [key: string]: string | number } {
		try {
			const options: ITealiumAnalyticsOptions = this.event.getOptions();
			const dataMap = TealiumConstants.DATA_MAPPING;
			const toolName = options ? get(options, 'toolName', GlobalAnalyticsMapperUtil.getToolName()) : null;
			const mode = options ? get(options, 'mode', GlobalAnalyticsMapperUtil.getMode()) : null;
			const partnerName = options ? get(options, 'partnerName', GlobalAnalyticsMapperUtil.getPartnerName()) : null;
			const importPartnerName = options ? get(options, 'importPartnerName', GlobalAnalyticsMapperUtil.getImportPartnerName()) : null;

			// TODO anyone: Remove unused constants after consultation with Analytics team
			const eventData = {
				[dataMap.COMPONENT_NAME]: !isEmpty(options.trackedComponentName) ? options.trackedComponentName : this.trackedComponentName,
				[dataMap.DIRTY_PORTFOLIO_ERRORS]: options.dirtyPortfolioErrors,
				[dataMap.ENGAGEMENT_ID]: options.engagementId,
				[dataMap.ERROR_SEVERITY]: options.errorSeverity,
				[dataMap.EXTRA_PARAMS]: options.extraParams,
				[dataMap.FTE_MAP]: options.fteMap,
				[dataMap.FTE_NEW_PORTFOLIO_ID]: options.fteNewPortfolioId,
				[dataMap.INCLUDE_YIELD]: options.includeYield,
				[dataMap.INSIGHT]: options.insight,
				[dataMap.MODE]: mode,
				[dataMap.MODULAR_VARS]: options.modularVars,
				[dataMap.PARAM]: options.param,
				[dataMap.PARTNER_NAME]: partnerName,
				[dataMap.PORTFOLIO_ADDED]: options.portfolioAdded,
				[dataMap.PORTFOLIO_CATEGORY]: options.portfolioCategory,
				[dataMap.PORTFOLIO_IDS]: options.portfolioIds,
				[dataMap.PORTFOLIO_REMOVED]: options.portfolioRemoved,
				[dataMap.REPORT_TYPE]: options.reportType,
				[dataMap.REPORT_VARS]: options.reportVars,
				[dataMap.TOOL_FLOW]: options.toolFlow,
				[dataMap.TOOL_NAME]: toolName,
				[dataMap.TOOL_PARAMS]: options.toolParams,
				[dataMap.TRACKED_ERROR_MESSAGE]: options.errorMessage,
				[dataMap.PORTFOLIOS_BREAKDOWN]: options.portfoliosBreakdown,
				[dataMap.FUND_HEALTH_ANALYSIS_BREAKDOWN]: options.fundHealthAnalysisBreakdown,
				[dataMap.PORTFOLIO_COUNT]: options.portfolioCount,
				[dataMap.IMPORT_PARTNER_NAME]: importPartnerName,
				[dataMap.INVALID_CUSIPS]: options.invalidCusips,
				[dataMap.INVALID_TICKERS]: options.invalidTickers,
				[dataMap.REPLACEMENT_CUSIPS]: options.replacementCusips
			};

			// Get rid of this when we do Adobe cleanup
			let linkName = cloneDeep(this.event.getEventName());
			if (linkName.includes('self service view') || linkName.includes('portfolio solutions view')) {
				linkName = linkName.replace('self service view', '').replace('portfolio solutions view', '');
			}

			eventData[dataMap.LINK_NAME] = options.linkName ? options.linkName : linkName;
			eventData[dataMap.LINK_TYPE] = linkName;

			if (TealiumConstants.TOOLS_EVENT.COMPLETION === this.event.getEventName()) {
				const portfolioIds = PortfolioIdResolverUtil.getPortfolioIds(new URLSearchParams(location.search));
				if (!options.portfolioIds && portfolioIds) {
					eventData[dataMap.PORTFOLIO_IDS] = portfolioIds;
				} else if (options.portfolioId) {
					eventData[dataMap.PORTFOLIO_IDS] = options.portfolioId;
				}
			}

			const engagementId = PortfolioIdResolverUtil.getEngagementId(new URLSearchParams(location.search));
			if (engagementId) {
				eventData[dataMap.ENGAGEMENT_ID] = engagementId;
			}

			const cleanedEventData = omitBy(eventData, value => isNil(value));
			return cleanedEventData;
		} catch (err) {
			console.log('Failed to construct event analytics', err);
		}
	}
}
